export type ThemeColorName =
  | "primary"
  | "secondary"
  | "tertiary"
  | "accent"
  | "error"
  | "success"
  | "warning"
  | "info"
  | "neutral"
  | "error-bg"
  | "error-bg-strong"
  | "success-bg"
  | "success-bg-strong"
  | "warning-bg"
  | "warning-bg-strong"
  | "border"
  | "white"
  | "subtle"
  | "sleep-text"
  | "blue-50"
  | "blue-100"
  | "blue-400"
  | "blue-600"
  | "blue-700"
  | "slate-100"
  | "slate-500"
  | "gray-500"
  | "green-50"
  | "emerald-500"
  | "orange-50"
  | "purple-500"
  | "indigo-500"
  | "pink-500"
  | "teal-500";

const cssColor = (name: ThemeColorName) => `var(--color-${name})`;
const accommodationVar = (index: number, name: string) =>
  `var(--color-accommodation-${index}-${name})`;
const legVar = (index: number, name: string) =>
  `var(--color-leg-${index}-${name})`;
const mapSafeThemeColors: Partial<Record<ThemeColorName, string>> = {
  primary: "#111418",
  secondary: "#617589",
  tertiary: "#8b949e",
  accent: "#f97316",
  error: "#ef4444",
  success: "#22c55e",
  warning: "#f59e0b",
  info: "#3b82f6",
  neutral: "#94a3b8",
  border: "#e5e7eb",
  white: "#ffffff",
  subtle: "#f0f2f4",
  "blue-50": "#eff6ff",
  "blue-100": "#dbeafe",
  "blue-400": "#60a5fa",
  "blue-600": "#2563eb",
  "blue-700": "#1d4ed8",
  "slate-100": "#f1f5f9",
  "slate-500": "#64748b",
  "gray-500": "#6b7280",
  "green-50": "#ecfdf5",
  "emerald-500": "#10b981",
  "orange-50": "#fff7ed",
  "purple-500": "#8b5cf6",
  "indigo-500": "#6366f1",
  "pink-500": "#ec4899",
  "teal-500": "#14b8a6",
};

export function themeColorAlpha(color: string, percent: number): string {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

export function resolveThemeColor(
  name: ThemeColorName,
  seen: Set<string> = new Set(),
): string {
  if (typeof document === "undefined") return cssColor(name);
  if (seen.has(name)) return cssColor(name);
  seen.add(name);

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--color-${name}`)
    .trim();
  const resolved = value ? resolveCssColor(value, seen) : "";

  // Mapbox GL's style parser is more reliable with concrete sRGB colors than
  // Tailwind v4's oklch()/color-mix() custom-property values.
  if (
    !resolved ||
    resolved.startsWith("var(") ||
    resolved.startsWith("oklch(") ||
    resolved.startsWith("color-mix(")
  ) {
    return mapSafeThemeColors[name] ?? cssColor(name);
  }

  return resolved;
}

export function resolveCssColor(
  color: string,
  seen: Set<string> = new Set(),
): string {
  const match = color.match(/^var\(--color-([^)]+)\)$/);
  if (!match) return color;
  return resolveThemeColor(match[1] as ThemeColorName, seen);
}

export const themeColors = {
  primary: cssColor("primary"),
  secondary: cssColor("secondary"),
  tertiary: cssColor("tertiary"),
  accent: cssColor("accent"),
  error: cssColor("error"),
  success: cssColor("success"),
  warning: cssColor("warning"),
  info: cssColor("info"),
  neutral: cssColor("neutral"),
  errorBg: cssColor("error-bg"),
  errorBgStrong: cssColor("error-bg-strong"),
  successBg: cssColor("success-bg"),
  successBgStrong: cssColor("success-bg-strong"),
  warningBg: cssColor("warning-bg"),
  warningBgStrong: cssColor("warning-bg-strong"),
  border: cssColor("border"),
  white: cssColor("white"),
  subtle: cssColor("subtle"),
  blue50: cssColor("blue-50"),
  blue100: cssColor("blue-100"),
  blue400: cssColor("blue-400"),
  blue600: cssColor("blue-600"),
  blue700: cssColor("blue-700"),
  slate100: cssColor("slate-100"),
  slate500: cssColor("slate-500"),
  gray500: cssColor("gray-500"),
  green50: cssColor("green-50"),
  emerald500: cssColor("emerald-500"),
  orange50: cssColor("orange-50"),
  purple500: cssColor("purple-500"),
  indigo500: cssColor("indigo-500"),
  pink500: cssColor("pink-500"),
  teal500: cssColor("teal-500"),
} as const;

export const tagColorPresets = [
  { color: themeColors.info, label: "Blue" },
  { color: themeColors.success, label: "Green" },
  { color: themeColors.warning, label: "Amber" },
  { color: themeColors.error, label: "Red" },
  { color: themeColors.purple500, label: "Purple" },
  { color: themeColors.pink500, label: "Pink" },
  { color: themeColors.teal500, label: "Teal" },
  { color: themeColors.accent, label: "Orange" },
] as const;

export const accommodationColorPickerOptions = [
  { index: 0, bg: themeColors.blue100, ring: themeColors.info },
  {
    index: 1,
    bg: themeColors.successBgStrong,
    ring: themeColors.success,
  },
  {
    index: 2,
    bg: themeColors.warningBgStrong,
    ring: themeColors.warning,
  },
  {
    index: 3,
    bg: themeColorAlpha(themeColors.pink500, 18),
    ring: themeColors.pink500,
  },
  {
    index: 4,
    bg: themeColorAlpha(themeColors.indigo500, 18),
    ring: themeColors.indigo500,
  },
  {
    index: 5,
    bg: themeColorAlpha(themeColors.accent, 18),
    ring: themeColors.accent,
  },
  {
    index: 6,
    bg: themeColorAlpha(themeColors.teal500, 16),
    ring: themeColors.teal500,
  },
  {
    index: 7,
    bg: themeColors.errorBgStrong,
    ring: themeColors.error,
  },
] as const;

export interface AccommodationPaletteEntry {
  bg: string;
  header: string;
  border: string;
  text: string;
  sleepBg: string;
  sleepBorder: string;
  sleepText: string;
  badgeBg: string;
  badgeText: string;
}

const accommodationPalette = (index: number): AccommodationPaletteEntry => ({
  bg: accommodationVar(index, "bg"),
  header: accommodationVar(index, "header"),
  border: accommodationVar(index, "border"),
  text: accommodationVar(index, "text"),
  sleepBg: accommodationVar(index, "sleep-bg"),
  sleepBorder: accommodationVar(index, "sleep-border"),
  sleepText: accommodationVar(index, "sleep-text"),
  badgeBg: accommodationVar(index, "badge-bg"),
  badgeText: accommodationVar(index, "badge-text"),
});

export const accommodationPalettes: AccommodationPaletteEntry[] = Array.from(
  { length: 8 },
  (_, index) => accommodationPalette(index),
);

export interface LegPaletteEntry {
  bg: string;
  border: string;
  text: string;
  dot: string;
}

const legPaletteAt = (index: number): LegPaletteEntry => ({
  bg: legVar(index, "bg"),
  border: legVar(index, "border"),
  text: legVar(index, "text"),
  dot: legVar(index, "dot"),
});

export const legPalettes: LegPaletteEntry[] = Array.from(
  { length: 8 },
  (_, index) => legPaletteAt(index),
);

/** Wraps out of range rather than falling back, so every leg gets a colour. */
export const legPalette = (index: number): LegPaletteEntry =>
  legPalettes[
    ((index % legPalettes.length) + legPalettes.length) % legPalettes.length
  ];

/** Swatch options for the leg colour picker. */
export const legColorPickerOptions = legPalettes.map((palette, index) => ({
  index,
  bg: palette.bg,
  ring: palette.dot,
}));

export const defaultAccommodationPalette: AccommodationPaletteEntry = {
  bg: themeColors.subtle,
  header: themeColors.white,
  border: themeColors.border,
  text: themeColors.secondary,
  sleepBg: themeColors.blue50,
  sleepBorder: themeColorAlpha(themeColors.info, 25),
  sleepText: cssColor("sleep-text"),
  badgeBg: themeColors.border,
  badgeText: themeColors.secondary,
};

export const actionToneConfig: Record<
  string,
  { icon: string; color: string; bg: string }
> = {
  create: {
    icon: "add_circle",
    color: themeColors.success,
    bg: themeColors.successBg,
  },
  update: {
    icon: "edit",
    color: themeColors.info,
    bg: themeColorAlpha(themeColors.info, 8),
  },
  delete: {
    icon: "delete",
    color: themeColors.error,
    bg: themeColors.errorBg,
  },
  schedule: {
    icon: "event",
    color: themeColors.purple500,
    bg: themeColorAlpha(themeColors.purple500, 8),
  },
  unschedule: {
    icon: "event_busy",
    color: themeColors.warning,
    bg: themeColors.warningBg,
  },
  reorder: {
    icon: "swap_vert",
    color: themeColors.indigo500,
    bg: themeColorAlpha(themeColors.indigo500, 8),
  },
  sleep_change: {
    icon: "hotel",
    color: themeColors.pink500,
    bg: themeColorAlpha(themeColors.pink500, 8),
  },
  undo: {
    icon: "undo",
    color: themeColors.neutral,
    bg: themeColorAlpha(themeColors.neutral, 8),
  },
  redo: {
    icon: "redo",
    color: themeColors.neutral,
    bg: themeColorAlpha(themeColors.neutral, 8),
  },
};

export const saveStatusToneConfig: Record<
  string,
  { icon: string; label: string; color: string; bg: string }
> = {
  saved: {
    icon: "cloud_done",
    label: "Saved",
    color: themeColors.success,
    bg: themeColors.successBg,
  },
  saving: {
    icon: "sync",
    label: "Saving...",
    color: themeColors.warning,
    bg: themeColors.warningBg,
  },
  unsaved: {
    icon: "pending",
    label: "Unsaved changes",
    color: themeColors.neutral,
    bg: themeColorAlpha(themeColors.neutral, 6),
  },
  error: {
    icon: "cloud_off",
    label: "Save failed",
    color: themeColors.error,
    bg: themeColors.errorBg,
  },
};

export const permissionToneConfig = {
  editor: { bg: themeColors.orange50, color: themeColors.accent },
  viewer: { bg: themeColors.blue50, color: themeColors.info },
} as const;

export const collectionVisibilityToneConfig = {
  private: { bg: themeColors.slate100, color: themeColors.slate500 },
  public: { bg: themeColors.green50, color: themeColors.emerald500 },
  shared: { bg: themeColors.orange50, color: themeColors.accent },
} as const;

export function getMapColors() {
  return {
    defaultMarker: resolveThemeColor("primary"),
    route: resolveThemeColor("info"),
    resultBadge: resolveThemeColor("primary"),
    selectedFill: resolveThemeColor("blue-600"),
    selectedStroke: resolveThemeColor("blue-400"),
    iconFill: resolveThemeColor("white"),
    popupTitle: resolveThemeColor("primary"),
    popupMeta: resolveThemeColor("gray-500"),
  } as const;
}
